/**
 * KOAI Chat Input Component
 * 채팅 입력 컴포넌트 - + 버튼으로 파일/사진 첨부 가능
 */

import { useState, useRef } from 'react';
import { Box, TextField, IconButton, CircularProgress, Tooltip, Typography } from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export default function ChatInput({ onSend, onAttach, isLoading, disabled }) {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleSend = () => {
    if (!message.trim() || isLoading || disabled) return;
    
    onSend(message.trim());
    setMessage('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAttach = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      onAttach?.(files);
    }
    // 같은 파일 재선택 가능하도록 초기화
    e.target.value = '';
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* 숨겨진 파일 입력 */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,.hwp,.hwpx,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tiff,.tif"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        {/* + 버튼 (파일 또는 사진 추가) */}
        {onAttach && (
          <Tooltip title="파일 또는 사진 추가">
            <IconButton
              onClick={handleAttach}
              disabled={isLoading || disabled}
              sx={{
                width: 40,
                height: 40,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '50%',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main'
                }
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
        
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          placeholder={isLoading ? '처리 중...' : t('chat.placeholder', '메시지를 입력하세요')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading || disabled}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3
            }
          }}
        />
        
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!message.trim() || isLoading || disabled}
          sx={{
            width: 40,
            height: 40,
            bgcolor: message.trim() ? 'primary.main' : 'grey.300',
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.dark'
            },
            '&.Mui-disabled': {
              bgcolor: 'grey.300',
              color: 'grey.500'
            }
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <SendIcon />
          )}
        </IconButton>
      </Box>
      
      {/* 안내 텍스트 */}
      {onAttach && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
          파일은 AI 분석 후 즉시 삭제됩니다. 서버에 저장되지 않습니다.
        </Typography>
      )}
    </Box>
  );
}
